import {IApplicationFormData, IWizardOtherOccupant} from './application-form'

const sameOccupant = (left: IWizardOtherOccupant, right: IWizardOtherOccupant): boolean =>
  (left.id !== undefined && right.id !== undefined && left.id === right.id) ||
  (!!left.clientId && left.clientId === right.clientId)

const sameDetails = (left: IWizardOtherOccupant, right: IWizardOtherOccupant): boolean =>
  (left.name ?? '') === (right.name ?? '') &&
  String(left.age ?? '') === String(right.age ?? '') &&
  (left.relationship ?? '') === (right.relationship ?? '')

export const buildOtherOccupantChanges = (data: IApplicationFormData) => ({
  upserts: (data.otherOccupants ?? []).filter((occupant) => {
    const original = data.otherOccupantBaseline?.find((item) => sameOccupant(item, occupant))
    return !original || !sameDetails(original, occupant)
  }),
  removals: data.removedOtherOccupants ?? [],
})

export const readSavedOtherOccupants = (
  saved: unknown,
  upserts: IWizardOtherOccupant[] = []
): IWizardOtherOccupant[] => {
  if (
    !Array.isArray(saved) ||
    saved.some(
      (item) =>
        !item ||
        typeof item !== 'object' ||
        !(
          (Number.isSafeInteger(item.id) && item.id > 0) ||
          (typeof item.clientId === 'string' && item.clientId.trim())
        )
    )
  ) {
    throw new Error(
      'The occupant save response is missing IDs. Please refresh the page and try again.'
    )
  }

  if (
    upserts.some(
      (item) => !item.id && item.clientId && !saved.some((entry) => sameOccupant(entry, item))
    )
  ) {
    throw new Error(
      'The saved occupant could not be matched to your entry. Please refresh the page.'
    )
  }

  return saved
}

export const reconcileSavedOtherOccupants = (
  current: IApplicationFormData,
  submitted: IApplicationFormData,
  saved: IWizardOtherOccupant[]
): Partial<IApplicationFormData> => {
  readSavedOtherOccupants(saved)
  const removed = current.removedOtherOccupants ?? []
  const occupants = current.otherOccupants.flatMap((occupant) => {
    const persisted = saved.find((item) => sameOccupant(item, occupant))
    const original = submitted.otherOccupants.find((item) => sameOccupant(item, occupant))
    if (persisted) {
      const baseline = submitted.otherOccupantBaseline?.find((item) => sameOccupant(item, occupant))
      // Refresh untouched shared rows, but keep submitted/pending input, including a blank age.
      const unchanged =
        original && baseline && sameDetails(original, baseline) && sameDetails(occupant, original)
      return [
        unchanged
          ? {...persisted}
          : {
              ...persisted,
              name: occupant.name,
              age: occupant.age,
              relationship: occupant.relationship,
            },
      ]
    }
    // Respect an acknowledged deletion without discarding an edit made during the save.
    return occupant.id !== undefined && original && sameDetails(occupant, original)
      ? []
      : [occupant]
  })

  saved.forEach((occupant) => {
    if (
      !occupants.some((item) => sameOccupant(item, occupant)) &&
      !submitted.otherOccupants.some((item) => sameOccupant(item, occupant)) &&
      !removed.some((item) => sameOccupant(item, occupant))
    ) {
      occupants.push(occupant)
    }
  })

  return {
    otherOccupants: occupants,
    otherOccupantBaseline: saved.map((item) => ({...item})),
    // Keep a removal made during the request for the next save.
    removedOtherOccupants: removed.filter(
      (item) =>
        !submitted.removedOtherOccupants?.some((entry) => sameOccupant(entry, item)) ||
        saved.some((entry) => sameOccupant(entry, item))
    ),
    hasOtherOccupants: occupants.length > 0,
  }
}
